/**
 * EditNotesDialog - Dialog para editar observações do cliente
 * Permite adicionar ou modificar as observações/notas sobre o cliente
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-14
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCrmCustomers, CrmCustomer } from '@/hooks/useCrmCustomers';
import { Loader2 } from 'lucide-react';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('EditNotesDialog');

// Props do componente
interface EditNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CrmCustomer;
  onSuccess?: () => void;
}

/**
 * Componente de dialog para edição de observações do cliente
 * 
 * @param open - Estado de visibilidade do dialog
 * @param onOpenChange - Callback para alterar estado de visibilidade
 * @param customer - Dados do cliente sendo editado
 * @param onSuccess - Callback executado após salvar com sucesso
 */
export function EditNotesDialog({
  open,
  onOpenChange,
  customer,
  onSuccess
}: EditNotesDialogProps) {
  // Estado local para o texto das observações
  const [notes, setNotes] = useState('');
  
  // Hook para atualização do cliente
  const { updateCustomer } = useCrmCustomers();

  // Inicializa o valor quando o dialog abre
  useEffect(() => {
    if (open) {
      setNotes(customer.notes || '');
      logger.debug('Dialog aberto, observações carregadas:', { 
        customerId: customer.id, 
        hasNotes: !!customer.notes 
      });
    }
  }, [open, customer.notes, customer.id]);

  // Flag de loading baseada no estado da mutation
  const isLoading = updateCustomer.isPending;

  /**
   * Handler para salvar as observações
   * Atualiza o campo notes do cliente no banco de dados
   */
  const handleSave = async () => {
    logger.debug('Salvando observações...', { customerId: customer.id });
    
    try {
      await updateCustomer.mutateAsync({
        id: customer.id,
        notes: notes.trim() || null,
      });
      logger.info('Observações atualizadas com sucesso', { customerId: customer.id });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('Erro ao atualizar observações:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Observações</DialogTitle>
          <DialogDescription>
            Adicione ou edite as observações sobre este cliente
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              placeholder="Digite suas observações sobre o cliente..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
